/**
 * TokenBreakdownPanel — a popover, opened from the footer TokenCounter, that
 * itemizes a conversation's token consumption per (model, provider).
 *
 * Reads the same per-(model, provider) data as CostBreakdownPanel from the
 * usage ledger (GET /conversations/{id}/cost-breakdown), but renders the token
 * side: input (fresh + cache) ↑ and output ↓, with a cache note. The "input"
 * headline counts cache reads/writes as input, matching the footer pill. Shares
 * its shell + helpers with the cost panel (see breakdownShared.tsx).
 *
 * Example row:
 *   Claude Opus 4.8 · Anthropic    ↑ 45K (cache 30K) · ↓ 12K
 */

import type { CostBreakdownRow } from "@/api/types";
import { ProviderIcon, getProvider } from "@/components/common/ProviderLogos";
import {
  BreakdownPopover,
  formatTokens,
  modelLabel,
  providerDisplayName,
  sourceTagList,
  useBreakdown,
} from "./breakdownShared";

/** Total input a model saw = fresh + both cache buckets (matches the pill). */
function inputTotal(t: CostBreakdownRow["tokens"]): number {
  return t.input + t.cache_read + t.cache_write;
}

function TokenCell({ row }: { row: CostBreakdownRow }) {
  const t = row.tokens;
  const cache = t.cache_read + t.cache_write;
  return (
    <span className="text-right text-[11px] tabular-nums text-space">
      <span className="font-semibold">↑ {formatTokens(inputTotal(t))}</span>
      {cache > 0 && <span className="text-space-40"> (cache {formatTokens(cache)})</span>}
      <span className="block text-[10px] text-space-40">↓ {formatTokens(t.output)}</span>
    </span>
  );
}

export function TokenBreakdownPanel({
  conversationId,
  onClose,
}: {
  conversationId: string;
  onClose: () => void;
}) {
  const { data, error } = useBreakdown(conversationId);

  const totals = (data?.rows ?? []).reduce(
    (acc, r) => {
      acc.in += inputTotal(r.tokens);
      acc.out += r.tokens.output;
      return acc;
    },
    { in: 0, out: 0 },
  );

  return (
    <BreakdownPopover title="Tokens by model" onClose={onClose}>
      {error && <p className="text-[11px] text-space-40">Couldn't load the breakdown.</p>}
      {!error && !data && <p className="text-[11px] text-space-40">Loading…</p>}
      {data && data.rows.length === 0 && (
        <p className="text-[11px] text-space-40">No token usage recorded yet.</p>
      )}

      {data && data.rows.length > 0 && (
        <>
          <ul className="flex flex-col divide-y divide-space-10">
            {[...data.rows]
              .sort((a, b) => inputTotal(b.tokens) - inputTotal(a.tokens))
              .map((row) => {
                const provider = row.provider || getProvider(row.model);
                const tags = sourceTagList(row.sources);
                return (
                  <li
                    key={`${row.model}:${row.provider}`}
                    className="flex items-start justify-between gap-3 py-1.5"
                  >
                    <span className="flex items-start gap-1.5 min-w-0">
                      <ProviderIcon model={row.model} className="w-3 h-3 mt-0.5 shrink-0 text-space-40" />
                      <span className="min-w-0">
                        <span className="block text-[11px] font-medium text-space truncate">
                          {modelLabel(row.model)}
                        </span>
                        <span className="block text-[10px] text-space-40">
                          {providerDisplayName(provider)}
                          {tags.length > 0 && ` · ${tags.join(", ")}`}
                        </span>
                      </span>
                    </span>
                    <TokenCell row={row} />
                  </li>
                );
              })}
          </ul>
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-space-20">
            <span className="text-[11px] font-semibold text-space">Total</span>
            <span className="text-[11px] font-semibold text-space tabular-nums">
              ↑ {formatTokens(totals.in)} · ↓ {formatTokens(totals.out)}
            </span>
          </div>
          <p className="text-[10px] text-space-40 mt-1.5 leading-snug">
            Input counts cached tokens; "cache" is the cached + cache-write portion.
          </p>
        </>
      )}
    </BreakdownPopover>
  );
}
